<template>
	<TabList
		v-model="tabSelectedValue"
		class="upselling-hotelpage"
		:tabs="tabListItems"
	/>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useStore } from '@/components/common/store';
import type { Board, SearchFormDataType } from '@/interfaces/search-form';
import { cloneDeep } from '@utils/utils';
import { getPriceCorrectCurrency } from '@/js/utils/priceUtils';
import type { SimplePrice } from '@/interfaces/checkout/offer-data';
import TabList from '@lmt-rpb/TabList/TabList.vue';
import SelectContentAnalytics from '@modules/analytics/SelectContentAnalytics';

const store = useStore();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultTypes = computed(() => store.state.types.board.filter((obj: any) => obj.showInMenu) as Board[]);
const BASE_ENTRY = {
	id: 0,
	showInMenu: true,
	value: '',
	label: 'Alle Angebote',
} as const;
const upSellingList = computed<Board[]>(() => {
	return [BASE_ENTRY, ...getBoardListUpdatedPrices()];
});
const boardTypes = computed(() => store.state.searchMask?.boardTypes || []);
const formData = computed((): SearchFormDataType => store.state.searchMask);
const upSellingResponseError = computed(() => formData.value.board.fetchError);

const boardsWithPrices = computed(() =>
	upSellingList.value.filter((item) => item.price).sort((a, b) => Number(a?.price?.Amount) - Number(b?.price?.Amount))
);
const tabListItems = computed(() =>
	upSellingList.value.map((item, index) => ({
		title: modifyBoardTypeText(item.label),
		subText: upSellingResponseError.value ? undefined : subText(item),
		value: item.id,
		disabled: isEntryDisabled(item),
		index,
	}))
);

const tabSelectedValue = computed({
	get() {
		return upSellingList.value.find((item) => item.value === boardTypes.value[0] || '')?.id ?? 0;
	},
	set(value: number) {
		const selectedBoardType = boardTypes.value[0] || '';
		const selected = upSellingList.value.find((item) => Number(item.id) === value)?.value;
		if (selectedBoardType !== selected) {
			store.commit('searchMask/updateFormData', { boardTypes: selected ? [selected] : [] });
		}

		const selectedTab = tabListItems.value.find((item) => item.value === value);
		if (selectedTab) {
			SelectContentAnalytics.selectContent({
				cId: 'upselling_item',
				cType: 'ux_hotelpage_click',
				index: selectedTab.index + 1,
			});
		}
	},
});

const isAllDeals = (item: Board) => item.id === 0;
const subText = (item: Board) => {
	if (isAllDeals(item) && lowestPrice.value) {
		return 'ab ' + lowestPrice.value;
	}
	const next = getNextPrice(item);
	if (item.price) {
		if (next?.price && next?.price.Amount < item.price.Amount) {
			return 'ab ' + getPriceCorrectCurrency(next?.price);
		}
		return 'ab ' + getPriceCorrectCurrency(item.price);
	}
	if (!isEntryDisabled(item)) {
		return 'ab ' + getPriceCorrectCurrency(next.price);
	}
	return 'nicht verfügbar';
};

const getBoardsGreaterThan = (arr: Board[], num: number) => arr.filter((element) => element.id > num);
const getNextPrice = (item: Board) => getBoardsGreaterThan(boardsWithPrices.value, item.id)?.[0];
const isEntryDisabled = (entry: Board) => {
	if (isAllDeals(entry) && !lowestPrice.value) return true;
	return (
		!entry.price && !upSellingResponseError.value && entry.id !== 0 && !getBoardsGreaterThan(boardsWithPrices.value, entry.id).length
	);
};

const getBoardListUpdatedPrices = () => {
	const updatedTypes = cloneDeep(defaultTypes.value);

	updatedTypes.forEach((item) => {
		item.price = formData.value.board.priceMap.get(item.value);
	});

	return updatedTypes;
};

const modifyBoardTypeText = (boardType: string) =>
	['Nur Übernachtung', 'All Inclusive', 'Alle Angebote'].includes(boardType) ? boardType : 'mind. ' + boardType;

const lowestPrice = computed(() => {
	let price = '';
	const prices = upSellingList.value.map((i) => i.price).filter((i) => i !== undefined) as SimplePrice[];
	if (prices.length) {
		const minAmount = Math.min(...prices.map((i) => i.Amount));
		const cheapestPrice = prices.find((p) => p.Amount === minAmount);
		price = getPriceCorrectCurrency(cheapestPrice!);
	}
	return price;
});
</script>
